import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { store } from "../../../../../store";
import { cloneDeep } from "lodash";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayItemTaint } from "../aws/CpArrayItemTaint";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { EllipsisOutlined } from "@ant-design/icons";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { NodePoolDetectedTags } from "../detectedTags";
import { NodePoolCoreButton } from "../nodePoolCoreButton";

interface Props {
  index: number;
}
export const OblivusNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const name = useCpFormValue(`oblivus.nodePools.${index}.name`);
  const flavor = useCpFormValue(`oblivus.nodePools.${index}.flavor`);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool{name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={[`oblivus.nodePools.${index}`]} />
          </CardTitle>
          <CardContent>
            <div>
              <NodePoolDetectedTags name={`oblivus.nodePools.${index}.labels`} />
              <CpFormItem>
                <CpLabel name={`oblivus.nodePools.${index}.name`}>Name</CpLabel>
                <CpInput name={`oblivus.nodePools.${index}.name`} style={{ width: 600 }} />
                <CpErrorWarning name={`oblivus.nodePools.${index}.name`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`oblivus.nodePools.${index}.flavor`}>Flavor</CpLabel>
                <Space.Compact>
                  <CpInput name={`oblivus.nodePools.${index}.flavor`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("flavor")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`oblivus.nodePools.${index}.flavor`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex gap-2">
                  <div className="flex flex-col">
                    <CpLabel name={`oblivus.nodePools.${index}.minSize`}>Min Size</CpLabel>
                    <CpInputNumber name={`oblivus.nodePools.${index}.minSize`} />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`oblivus.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                    <CpInputNumber name={`oblivus.nodePools.${index}.maxSize`} />
                  </div>
                </div>
                <CpErrorWarningArray
                  names={[`oblivus.nodePools.${index}.minSize`, `oblivus.nodePools.${index}.maxSize`]}
                />
              </CpFormItem>
              <div style={{ maxWidth: 900 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Labels</CpLabel>
                    <NodePoolCoreButton name={`oblivus.nodePools.${index}.labels`} />
                  </div>
                  <div className="-mt-2">
                    <CpArrayObject
                      factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                      labels={["Name", "Value"]}
                      name={`oblivus.nodePools.${index}.labels`}
                    >
                      <CpArrayItemNameValuePair />
                    </CpArrayObject>
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                  labels={["Key", "Value", "Effect"]}
                  label="Taints"
                  name={`oblivus.nodePools.${index}.taints`}
                >
                  <CpArrayItemTaint />
                </CpArrayObject>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "flavor" ? (
        <NGProviderBrowser
          // TODO pagination for all discovery endpoints with pages
          title="Browse Oblivus Flavors"
          initialValue={flavor}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "instance-types-flat",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          sortable
          onOk={(value) => {
            formSetValue(formId, `oblivus.nodePools.${index}.flavor`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          filteredColumns={["Flavor"]}
          initialSort={["Cost per Month", "asc"]}
          rightAlign={["Cost per Month"]}
          monoColumns={["Cost per Month"]}
          labels={[
            "Flavor",
            "Cost per Month",
            "Cores",
            "Memory",
            "Root Storage",
            "Ephemeral Storage",
            "GPUs",
            "Network",
          ]}
          distinctFilters={["Cores", "Memory", "Root Storage", "Ephemeral Storage", "GPUs", "Network"]}
          getData={(item: any) => {
            const pricePerDay = Number(item.hourlyCost || 0) * 24;
            const pricePerMonth = pricePerDay * 30;
            const cost = pricePerMonth.toLocaleString("en-US", { style: "currency", currency: "USD" });

            return [
              item.flavor,
              item.flavor,
              [pricePerMonth, cost],
              item.cores,
              [item.memory, formatSizeForProviderBrowser(item.memory)],
              [item.rootStorage, formatSizeForProviderBrowser(item.rootStorage)],
              [item.ephemeralStorage, formatSizeForProviderBrowser(item.ephemeralStorage)],
              item.gpus,
              [item.network, `${item.network}GbE`],
            ];
          }}
        />
      ) : null}
    </>
  );
};
